/**
 * SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/tenant-admin/UnitTypesTab.tsx
 * PURPOSE: Release v0 WP-7 SC-008-UNIT-TYPE — tab "Loại đơn vị" trong Cài đặt tổ chức
 *          + BR-ORG-002/BS-008-R08/R09 field "Loại cha hợp lệ" (multi-select,
 *          junction table — quyết định Hang Le 2026-08-11: 1 loại có thể có
 *          NHIỀU cha hợp lệ, khác wireframe gốc vẽ dropdown-đơn)
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-23
 * COMPLIANCE: SDLC 6.5.0 + Zero Mock Tolerance — nối API THẬT /tenant-admin/unit-types
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs
 *
 * @business-context
 * - Domain: Tenant Administration, Organization Configuration
 * - Impact: Danh mục loại đơn vị nuôi dropdown "Loại đơn vị" ở SC-006 + cột "Mã loại" khi Nhập tệp.
 *   "Loại cha hợp lệ" ràng buộc "loại X lồng dưới loại Y" (BR-ORG-002 FR-002).
 * - Stakeholders: Tenant owners, tenant admins
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Tab Component của TenantSettings)
 * - Pattern: apiClient (theo ShiftTypesTab), inline styles, token var(--nqh-*), toast 4s.
 *   Multi-select "Loại cha hợp lệ" = checkbox list (KHÔNG phải dropdown-đơn như
 *   wireframe gốc vẽ — junction table cho phép N cha, xem @cross-references) —
 *   giữ đúng Ý ĐỊNH wireframe (chọn "loại cha hợp lệ" + banner lỗi vòng lặp),
 *   điều chỉnh control cho khớp hướng kỹ thuật mới.
 * - Dependencies: React 18, apiClient, backend/app/api/v2/tenant_admin/unit_types.py
 *
 * @cross-references
 * - Mockup: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/tenant-admin/settings-unit-type.html
 * - Wireframe: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §2 SC-008-UNIT-TYPE "🟡 BỔ SUNG 2026-08-11" ([5c]/[5d]/[5e])
 * - Business source: docs/01-planning/01-requirements/10-organization/BR-ORG-002-UNIT-TYPE-LEVEL-RELATIONSHIP.md
 * - API: backend/app/api/v2/tenant_admin/unit_types.py
 * - Host: frontend/src/pages/tenant-admin/TenantSettings.tsx (TAB_CONFIG)
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../../services/api/client";
import { API_BASE_URL } from "../../services/api/config";
import { PortalMenu, type PortalMenuAnchor } from "../../components/common/PortalMenu";

/** Resolve API base URL from the canonical single source (services/api/config.ts). */
const getApiBase = (version: string) => `${API_BASE_URL}/api/${version}`;

interface UnitType {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  in_use_count: number;
  allowed_parent_type_ids: string[];
  // DD-0102 §2 [5g] (PROPOSED — triển khai theo chỉ đạo trực tiếp user
  // 2026-08-25, DD chưa được @cto ratify chính thức).
  gps_eligible: boolean;
}

type UnitTypeForm = {
  code: string; name: string; is_active: boolean; allowed_parent_type_ids: string[];
  gps_eligible: boolean;
};
const EMPTY_FORM: UnitTypeForm = {
  code: "", name: "", is_active: true, allowed_parent_type_ids: [],
  gps_eligible: false,
};

// Token var(--nqh-*) từ styles/nqh-design-tokens.css (main.tsx import global),
// kèm fallback hex theo palette hiện hữu của TenantSettings.
const st = {
  card: {
    background: "var(--nqh-bg-surface, white)",
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  } as React.CSSProperties,
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "16px",
    flexWrap: "wrap" as const,
  } as React.CSSProperties,
  headTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
  } as React.CSSProperties,
  btnPrimary: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    color: "white",
    background: "var(--nqh-coffee-500, #d97706)",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  } as React.CSSProperties,
  btnSecondary: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    color: "var(--nqh-gray-700, #374151)",
    background: "var(--nqh-gray-200, #e5e7eb)",
  } as React.CSSProperties,
  tableWrap: {
    overflowX: "auto" as const,
    border: "1px solid var(--nqh-gray-200, #e5e7eb)",
    borderRadius: "12px",
    background: "var(--nqh-bg-surface, white)",
  } as React.CSSProperties,
  table: {
    borderCollapse: "separate" as const,
    borderSpacing: 0,
    width: "100%",
    minWidth: "560px",
    fontSize: "14px",
  } as React.CSSProperties,
  th: {
    background: "var(--nqh-bg-sunken, #f9fafb)",
    color: "var(--nqh-gray-700, #374151)",
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: ".02em",
    borderBottom: "1px solid var(--nqh-gray-200, #e5e7eb)",
    padding: "8px 12px",
    textAlign: "left" as const,
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  td: {
    borderBottom: "1px solid var(--nqh-gray-100, #f3f4f6)",
    padding: "8px 12px",
    textAlign: "left" as const,
    verticalAlign: "middle" as const,
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  codeCell: {
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
    fontVariantNumeric: "tabular-nums" as const,
  } as React.CSSProperties,
  statusOk: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    fontWeight: 600,
    color: "var(--nqh-herb-700, #15803d)",
  } as React.CSSProperties,
  statusOff: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    color: "var(--nqh-gray-500, #6b7280)",
  } as React.CSSProperties,
  iconBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px 8px",
    color: "var(--nqh-gray-500, #6b7280)",
    fontSize: "16px",
    lineHeight: 1,
    borderRadius: "6px",
  } as React.CSSProperties,
  menu: {
    position: "absolute" as const,
    right: 0,
    top: "100%",
    zIndex: 50,
    background: "white",
    border: "1px solid var(--nqh-gray-200, #e5e7eb)",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    minWidth: "160px",
    padding: "4px",
  } as React.CSSProperties,
  menuItem: {
    display: "block",
    width: "100%",
    textAlign: "left" as const,
    background: "none",
    border: "none",
    padding: "8px 12px",
    fontSize: "14px",
    color: "var(--nqh-gray-700, #374151)",
    cursor: "pointer",
    borderRadius: "6px",
  } as React.CSSProperties,
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: "white",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "480px",
    width: "90%",
  } as React.CSSProperties,
  modalTitle: {
    margin: "0 0 16px",
    fontSize: "18px",
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
  } as React.CSSProperties,
  bannerError: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "var(--nqh-chili-050, #fef2f2)",
    border: "1px solid var(--nqh-chili-200, #fecaca)",
    borderRadius: "8px",
    padding: "10px 12px",
    marginBottom: "16px",
    color: "var(--nqh-chili, #dc2626)",
    fontSize: "14px",
  } as React.CSSProperties,
  field: { marginBottom: "16px" } as React.CSSProperties,
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: 500,
    color: "var(--nqh-gray-700, #374151)",
    marginBottom: "6px",
  } as React.CSSProperties,
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid var(--nqh-gray-300, #d1d5db)",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box" as const,
  } as React.CSSProperties,
  fieldError: {
    marginTop: "4px",
    fontSize: "12px",
    color: "var(--nqh-chili, #dc2626)",
  } as React.CSSProperties,
  radios: {
    display: "flex",
    gap: "16px",
    fontSize: "14px",
    color: "var(--nqh-gray-700, #374151)",
  } as React.CSSProperties,
  radioLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    minHeight: "32px",
  } as React.CSSProperties,
  // [5c] "Loại cha hợp lệ" — multi-select checkbox list (BS-008-R08, junction
  // table cho phép N cha, khác dropdown-đơn wireframe gốc).
  parentTypeList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
    maxHeight: "160px",
    overflowY: "auto" as const,
    border: "1px solid var(--nqh-gray-300, #d1d5db)",
    borderRadius: "8px",
    padding: "8px 12px",
  } as React.CSSProperties,
  parentTypeEmpty: {
    fontSize: "13px",
    color: "var(--nqh-gray-500, #6b7280)",
    padding: "4px 0",
  } as React.CSSProperties,
  parentTypeHint: {
    fontSize: "12px",
    color: "var(--nqh-gray-500, #6b7280)",
    marginTop: "4px",
  } as React.CSSProperties,
  presetBanner: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    background: "var(--nqh-coffee-050, #fffbeb)",
    border: "1px solid var(--nqh-coffee-200, #fde68a)",
    borderRadius: "8px",
    padding: "10px 12px",
    marginBottom: "16px",
    fontSize: "13px",
    color: "var(--nqh-gray-700, #374151)",
  } as React.CSSProperties,
  presetBannerActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  } as React.CSSProperties,
  modalFoot: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "8px",
  } as React.CSSProperties,
  spinner: {
    display: "inline-block",
    width: "14px",
    height: "14px",
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "nqh-ut-spin 0.8s linear infinite",
  } as React.CSSProperties,
  toast: {
    position: "fixed" as const,
    bottom: "24px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "var(--nqh-gray-900, #1f2937)",
    color: "white",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    zIndex: 1100,
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  } as React.CSSProperties,
  skeletonRow: {
    height: "20px",
    borderRadius: "6px",
    background: "var(--nqh-gray-100, #f3f4f6)",
    margin: "12px",
  } as React.CSSProperties,
};

const UnitTypesTab: React.FC = () => {
  const [items, setItems] = useState<UnitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // F-8 fix: anchor rect cho PortalMenu
  const [menuAnchor, setMenuAnchor] = useState<PortalMenuAnchor | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Modal [5] Thêm/Sửa
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<UnitType | null>(null);
  const [form, setForm] = useState<UnitTypeForm>(EMPTY_FORM);
  const [codeErr, setCodeErr] = useState(false);
  const [nameErr, setNameErr] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // [5d] BS-008-R10: banner gợi ý preset 4 loại chuẩn — chỉ modal Thêm, chỉ khi
  // danh mục rỗng, ẩn được trong phiên mở modal hiện tại ("Để sau").
  const [hidePresetBanner, setHidePresetBanner] = useState(false);
  const [applyingPreset, setApplyingPreset] = useState(false);

  // Dialog [6] chặn xoá
  const [blockMsg, setBlockMsg] = useState<string | null>(null);
  // bot-119 (#1019): dialog xác nhận Ngừng/Kích hoạt — DÙNG LẠI đúng khuôn
  // dialog [6] ngay dưới (st.overlay + st.modal + st.modalFoot) và cơ chế
  // toast sẵn có của tab này; KHÔNG dựng component mới. Cùng họ bot-077
  // (chi nhánh) + bot-088 (người dùng): thao tác ghi im lặng, không confirm,
  // không feedback → TA phải F5 mới biết đã lưu chưa.
  const [confirmToggle, setConfirmToggle] = useState<UnitType | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // C6 guard (TEST-QUALITY idempotency/double-action): khoá nút Xoá của ĐÚNG
  // dòng đang có request DELETE bay — không có state này, request chậm +
  // người dùng mở lại menu ⁝ bấm "Xoá" lần 2 sẽ bắn 2 lệnh DELETE trùng cho
  // cùng 1 id. Không khoá vĩnh viễn sau lỗi 409 — BS-008-R05 chủ ý cho bấm lại
  // "Hãy đổi loại các đơn vị đó trước khi xoá" (retry sau khi đổi loại đơn vị).
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const menuRef = useRef<HTMLDivElement | null>(null);

  const fetchItems = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await apiClient.get("/tenant-admin/unit-types", { baseURL: getApiBase("v2") });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setLoadError("Không tải được danh mục loại · Thử lại");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Toast tự ẩn sau 4s (pattern window.nqhToast trong mockup common.js)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Đóng ⁝ menu khi click ra ngoài
  useEffect(() => {
    if (!openMenuId) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openMenuId]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setCodeErr(false); setNameErr(false); setBanner(null);
    setHidePresetBanner(false);
    setShowModal(true);
  };

  const openEdit = (item: UnitType) => {
    setEditing(item);
    setForm({
      code: item.code, name: item.name, is_active: item.is_active,
      allowed_parent_type_ids: item.allowed_parent_type_ids,
      gps_eligible: item.gps_eligible,
    });
    setCodeErr(false); setNameErr(false); setBanner(null);
    setOpenMenuId(null);
    setShowModal(true);
  };

  const toggleParentType = (id: string) => {
    setForm((f) => ({
      ...f,
      allowed_parent_type_ids: f.allowed_parent_type_ids.includes(id)
        ? f.allowed_parent_type_ids.filter((p) => p !== id)
        : [...f.allowed_parent_type_ids, id],
    }));
  };

  const handleSave = async () => {
    const badCode = !form.code.trim();
    const badName = !form.name.trim();
    setCodeErr(badCode); setNameErr(badName); setBanner(null);
    if (badCode || badName) return;

    setSaving(true);
    try {
      if (editing) {
        await apiClient.put(
          `/tenant-admin/unit-types/${editing.id}`,
          {
            name: form.name.trim(), is_active: form.is_active,
            allowed_parent_type_ids: form.allowed_parent_type_ids,
            gps_eligible: form.gps_eligible,
          },
          { baseURL: getApiBase("v2") },
        );
      } else {
        await apiClient.post(
          "/tenant-admin/unit-types",
          {
            code: form.code.trim().toUpperCase(), name: form.name.trim(), is_active: form.is_active,
            allowed_parent_type_ids: form.allowed_parent_type_ids,
            gps_eligible: form.gps_eligible,
          },
          { baseURL: getApiBase("v2") },
        );
      }
      setShowModal(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      await fetchItems();
      setToast("Đã lưu loại đơn vị");
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      // [5b]/[5e] banner lỗi — trùng mã (409) / vòng lặp+cross-tenant (400,
      // BS-008-R09) / lưu thất bại server
      setBanner(typeof msg === "string" ? msg : "Lưu loại đơn vị thất bại. Vui lòng thử lại.");
    } finally {
      setSaving(false);
    }
  };

  // [5d] BS-008-R10: "Dùng 4 loại chuẩn" — tạo tuần tự 4 loại mặc định, mỗi
  // loại tự set allowed_parent_type_ids đúng thứ tự preset (BS-008-D03).
  // KHÔNG ép buộc / KHÔNG auto-tạo ngoài hành động bấm rõ ràng của TA (không
  // vi phạm R07 "danh mục mặc định RỖNG").
  const applyPreset = async () => {
    setApplyingPreset(true);
    setBanner(null);
    try {
      const holding = await apiClient.post(
        "/tenant-admin/unit-types",
        { code: "HOLDING", name: "Tập đoàn", is_active: true, allowed_parent_type_ids: [] },
        { baseURL: getApiBase("v2") },
      );
      const opco = await apiClient.post(
        "/tenant-admin/unit-types",
        { code: "OPCO", name: "Công ty vận hành", is_active: true, allowed_parent_type_ids: [holding.data.id] },
        { baseURL: getApiBase("v2") },
      );
      const branch = await apiClient.post(
        "/tenant-admin/unit-types",
        { code: "BRANCH", name: "Cơ sở", is_active: true, allowed_parent_type_ids: [opco.data.id] },
        { baseURL: getApiBase("v2") },
      );
      await apiClient.post(
        "/tenant-admin/unit-types",
        { code: "WORKAREA", name: "Khu vực làm việc", is_active: true, allowed_parent_type_ids: [branch.data.id] },
        { baseURL: getApiBase("v2") },
      );
      setShowModal(false);
      setForm(EMPTY_FORM);
      await fetchItems();
      setToast("Đã tạo 4 loại đơn vị chuẩn");
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      setBanner(typeof msg === "string" ? msg : "Không tạo được 4 loại chuẩn. Vui lòng thử lại.");
    } finally {
      setApplyingPreset(false);
    }
  };

  // bot-119: chỉ chạy SAU khi TA xác nhận trong dialog; kết thúc luôn có toast
  // (thành công hoặc lỗi) — không còn "bấm là đổi ngay, không biết đã lưu chưa".
  const handleToggle = async (item: UnitType) => {
    if (togglingId) return;
    setTogglingId(item.id);
    try {
      // Two explicit endpoints (activate / deactivate) rather than a computed
      // `${action}` segment — keeps each call statically resolvable against the
      // backend route manifest (frontend↔backend contract check S106L-004).
      if (item.is_active) {
        await apiClient.post(`/tenant-admin/unit-types/${item.id}/deactivate`, {}, { baseURL: getApiBase("v2") });
      } else {
        await apiClient.post(`/tenant-admin/unit-types/${item.id}/activate`, {}, { baseURL: getApiBase("v2") });
      }
      setConfirmToggle(null);
      await fetchItems();
      setToast(item.is_active ? "Đã ngừng hoạt động loại đơn vị" : "Đã kích hoạt loại đơn vị");
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      setConfirmToggle(null);
      setToast(typeof msg === "string" ? msg : "Thao tác thất bại");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (item: UnitType) => {
    setOpenMenuId(null);
    // C6 guard: request DELETE cho dòng này đang bay → bỏ qua lần bấm lặp
    if (deletingId === item.id) return;
    setDeletingId(item.id);
    try {
      await apiClient.delete(`/tenant-admin/unit-types/${item.id}`, { baseURL: getApiBase("v2") });
      setItems((prev) => prev.filter((it) => it.id !== item.id));
      setToast("Đã xoá loại đơn vị");
    } catch (err: any) {
      if (err?.response?.status === 409) {
        // Dialog [6] chặn xoá — body lấy verbatim từ server
        const msg = err?.response?.data?.detail;
        setBlockMsg(
          typeof msg === "string"
            ? msg
            : `Loại "${item.name}" đang được ${item.in_use_count} đơn vị sử dụng. Hãy đổi loại các đơn vị đó trước khi xoá.`,
        );
      } else {
        const msg = err?.response?.data?.detail;
        setToast(typeof msg === "string" ? msg : "Xoá thất bại");
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div style={st.card} data-testid="unit-types-skeleton">
        <div style={st.head}>
          <h3 style={st.headTitle}>Loại đơn vị</h3>
        </div>
        <div style={st.tableWrap}>
          {[0, 1, 2, 3].map((i) => <div key={i} style={st.skeletonRow} />)}
        </div>
      </div>
    );
  }

  return (
    <div style={st.card}>
      {/* keyframes cho spinner */}
      <style>{`@keyframes nqh-ut-spin { to { transform: rotate(360deg); } }`}</style>

      {/* [2] Tiêu đề + [3] + Thêm loại (primary, đẩy phải) */}
      <div style={st.head}>
        <h3 style={st.headTitle}>Loại đơn vị</h3>
        <button type="button" style={st.btnPrimary} onClick={openCreate} data-testid="btn-add-unit-type">
          + Thêm loại
        </button>
      </div>

      {loadError && (
        <div style={{ ...st.bannerError, justifyContent: "space-between" }} role="alert">
          <span>{loadError}</span>
          <button type="button" style={st.btnSecondary} onClick={() => { setLoading(true); fetchItems(); }}>
            Thử lại
          </button>
        </div>
      )}

      {/* [4] Bảng loại đơn vị — 4 cột */}
      {!loadError && (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th} scope="col">Mã loại</th>
                <th style={st.th} scope="col">Tên loại</th>
                <th style={st.th} scope="col">Trạng thái</th>
                <th style={{ ...st.th, width: "56px", textAlign: "center" }} scope="col">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={{ ...st.td, ...st.codeCell }}>{item.code}</td>
                  <td style={st.td}>{item.name}</td>
                  <td style={st.td}>
                    {item.is_active ? (
                      <span style={st.statusOk}>● Hoạt động</span>
                    ) : (
                      <span style={st.statusOff}>○ Ngừng</span>
                    )}
                  </td>
                  <td style={{ ...st.td, textAlign: "center" }}>
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                      ref={openMenuId === item.id ? menuRef : undefined}
                    >
                      <button
                        type="button"
                        style={st.iconBtn}
                        aria-haspopup="true"
                        aria-expanded={openMenuId === item.id}
                        aria-label={`Thao tác — ${item.code}`}
                        disabled={deletingId === item.id}
                        onClick={(e) => {
                          if (openMenuId === item.id) { setOpenMenuId(null); return; }
                          const r = e.currentTarget.getBoundingClientRect();
                          setMenuAnchor({ top: r.top, bottom: r.bottom, right: window.innerWidth - r.right });
                          setOpenMenuId(item.id);
                        }}
                      >
                        ⋮
                      </button>
                      {/* F-8 fix: PortalMenu — menu không bị tableWrap overflow cắt */}
                      {openMenuId === item.id && menuAnchor && (
                        <PortalMenu anchor={menuAnchor} onClose={() => setOpenMenuId(null)} style={{ padding: "4px" }}>
                          <button type="button" style={st.menuItem} role="menuitem" onClick={() => openEdit(item)}>
                            Sửa
                          </button>
                          <button
                            type="button"
                            style={st.menuItem}
                            role="menuitem"
                            onClick={() => { setOpenMenuId(null); setConfirmToggle(item); }}
                          >
                            {item.is_active ? "Ngừng hoạt động" : "Kích hoạt"}
                          </button>
                          <button
                            type="button"
                            style={{
                              ...st.menuItem,
                              color: "var(--nqh-chili, #dc2626)",
                              opacity: deletingId === item.id ? 0.6 : 1,
                              cursor: deletingId === item.id ? "not-allowed" : "pointer",
                            }}
                            role="menuitem"
                            disabled={deletingId === item.id}
                            onClick={() => handleDelete(item)}
                          >
                            {deletingId === item.id ? "Đang xoá…" : "Xoá"}
                          </button>
                        </PortalMenu>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && (
            <p style={{ textAlign: "center", color: "var(--nqh-gray-400, #9ca3af)", padding: "20px" }}>
              Chưa có loại đơn vị nào. Bấm + Thêm loại để tạo.
            </p>
          )}
        </div>
      )}

      {/* [5] Modal Thêm/Sửa loại đơn vị — giữa màn ~480px */}
      {showModal && (
        <div style={st.overlay} onClick={() => !saving && setShowModal(false)}>
          <div
            style={st.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="unitTypeModalTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <h2 style={st.modalTitle} id="unitTypeModalTitle">
                {editing ? "Sửa loại đơn vị" : "Thêm loại đơn vị"}
              </h2>
              {/* UT-1 (2026-07-30): D6 — modal có [✕] góc trên */}
              <button
                type="button"
                aria-label="Đóng"
                disabled={saving}
                onClick={() => setShowModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "var(--nqh-gray-500, #6b7280)", padding: "2px" }}
              >
                ✕
              </button>
            </div>

            {/* [5b]/[5e] banner lỗi — trùng mã (409) / vòng lặp+cross-tenant (400) / lưu thất bại */}
            {banner && (
              <div style={st.bannerError} role="alert" data-testid="unit-type-banner">
                ⚠ {banner}
              </div>
            )}

            {/* [5d] BS-008-R10: gợi ý preset — chỉ modal Thêm, chỉ khi danh mục rỗng */}
            {!editing && items.length === 0 && !hidePresetBanner && (
              <div style={st.presetBanner} data-testid="unit-type-preset-banner">
                <span>
                  💡 Bắt đầu nhanh với 4 loại chuẩn? Tập đoàn → Công ty vận hành → Cơ sở → Khu vực
                  làm việc — bạn sửa lại được sau.
                </span>
                <div style={st.presetBannerActions}>
                  <button
                    type="button"
                    style={st.btnSecondary}
                    disabled={applyingPreset}
                    onClick={() => setHidePresetBanner(true)}
                  >
                    Để sau
                  </button>
                  <button
                    type="button"
                    style={st.btnPrimary}
                    disabled={applyingPreset}
                    onClick={applyPreset}
                  >
                    {applyingPreset && <span style={st.spinner} aria-hidden="true" />}
                    Dùng 4 loại chuẩn
                  </button>
                </div>
              </div>
            )}

            <div style={st.field}>
              <label style={st.label} htmlFor="fTypeCode">Mã loại *</label>
              <input
                id="fTypeCode"
                style={{
                  ...st.input,
                  borderColor: codeErr ? "var(--nqh-chili, #dc2626)" : undefined,
                }}
                type="text"
                placeholder="vd: REGION"
                value={form.code}
                disabled={!!editing || saving}
                maxLength={50}
                onChange={(e) =>
                  setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "") })
                }
              />
              {codeErr && <div style={st.fieldError}>Nhập mã loại</div>}
            </div>

            <div style={st.field}>
              <label style={st.label} htmlFor="fTypeName">Tên loại *</label>
              <input
                id="fTypeName"
                style={{
                  ...st.input,
                  borderColor: nameErr ? "var(--nqh-chili, #dc2626)" : undefined,
                }}
                type="text"
                placeholder="vd: Vùng"
                value={form.name}
                disabled={saving}
                maxLength={100}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              {nameErr && <div style={st.fieldError}>Nhập tên loại</div>}
            </div>

            {/* [5c] BS-008-R08: "Loại cha hợp lệ" — multi-select (junction table,
                cho phép N cha, KHÔNG bắt buộc chọn ≥1 — bỏ trống = "Không có,
                là gốc cây"). Loại trừ chính loại đang sửa (chặn tự-chọn-mình ở
                FE; validate CHẶN THẬT vẫn ở API, BS-008-R09). */}
            <div style={st.field}>
              <label style={st.label} id="fParentTypesLabel">Loại cha hợp lệ</label>
              {items.filter((i) => i.id !== editing?.id).length === 0 ? (
                <div style={st.parentTypeEmpty}>
                  Chưa có loại nào khác để chọn làm cha — bỏ trống nghĩa là "Không có (là gốc cây)".
                </div>
              ) : (
                <div style={st.parentTypeList} role="group" aria-labelledby="fParentTypesLabel">
                  {items
                    .filter((i) => i.id !== editing?.id)
                    .map((i) => (
                      <label key={i.id} style={st.radioLabel}>
                        <input
                          type="checkbox"
                          checked={form.allowed_parent_type_ids.includes(i.id)}
                          disabled={saving}
                          onChange={() => toggleParentType(i.id)}
                        />
                        {i.name} ({i.code})
                      </label>
                    ))}
                </div>
              )}
              <div style={st.parentTypeHint}>
                {form.allowed_parent_type_ids.length === 0
                  ? "Bỏ trống = Không có (là gốc cây)."
                  : `Đã chọn ${form.allowed_parent_type_ids.length} loại cha hợp lệ.`}
              </div>
            </div>

            {/* [5g] DD-0102 §2 (PROPOSED — triển khai theo chỉ đạo trực tiếp user
                2026-08-25, DD chưa được @cto ratify chính thức): "Cho phép cấu
                hình GPS/Geofence cho loại này" — lưu unit_types.gps_eligible,
                mặc định TẮT. Vị trí theo wireframe: ngay dưới [5f] (chưa triển
                khai, ngoài phạm vi GPS), trên Trạng thái. */}
            <div style={st.field}>
              <label style={st.radioLabel} htmlFor="fGpsEligible">
                <input
                  id="fGpsEligible"
                  type="checkbox"
                  checked={form.gps_eligible}
                  disabled={saving}
                  onChange={(e) => setForm({ ...form, gps_eligible: e.target.checked })}
                />
                Cho phép cấu hình GPS/Geofence cho loại này
              </label>
            </div>

            <div style={st.field}>
              <label style={st.label}>Trạng thái</label>
              <div style={st.radios} role="radiogroup" aria-label="Trạng thái">
                <label style={st.radioLabel}>
                  <input
                    type="radio"
                    name="unitTypeStatus"
                    checked={form.is_active}
                    disabled={saving}
                    onChange={() => setForm({ ...form, is_active: true })}
                  />
                  Đang hoạt động
                </label>
                <label style={st.radioLabel}>
                  <input
                    type="radio"
                    name="unitTypeStatus"
                    checked={!form.is_active}
                    disabled={saving}
                    onChange={() => setForm({ ...form, is_active: false })}
                  />
                  Ngừng hoạt động
                </label>
              </div>
            </div>

            <div style={st.modalFoot}>
              <button type="button" style={st.btnSecondary} disabled={saving} onClick={() => setShowModal(false)}>
                Hủy
              </button>
              <button type="button" style={st.btnPrimary} disabled={saving} onClick={handleSave}>
                {saving && <span style={st.spinner} aria-hidden="true" />}
                {editing ? "Lưu thay đổi" : "Tạo loại"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* bot-119 (#1019) Dialog xác nhận Ngừng/Kích hoạt — cùng khuôn dialog [6] */}
      {confirmToggle && (
        <div style={st.overlay} onClick={() => !togglingId && setConfirmToggle(null)}>
          <div
            style={st.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirmToggleTitle"
            onClick={(e) => e.stopPropagation()}
            data-testid="unit-type-toggle-confirm"
          >
            <h2 style={st.modalTitle} id="confirmToggleTitle">
              {confirmToggle.is_active ? "Ngừng hoạt động loại đơn vị?" : "Kích hoạt loại đơn vị?"}
            </h2>
            <p style={{ margin: "0 0 16px", fontSize: "14px", color: "var(--nqh-gray-700, #374151)" }}>
              {confirmToggle.is_active
                ? `Loại "${confirmToggle.name}" (${confirmToggle.code}) sẽ chuyển sang trạng thái Ngừng. Các đơn vị đang dùng loại này không bị ảnh hưởng, nhưng loại sẽ không còn được chọn khi tạo đơn vị mới.`
                : `Loại "${confirmToggle.name}" (${confirmToggle.code}) sẽ chuyển sang trạng thái Hoạt động và được chọn lại khi tạo đơn vị mới.`}
            </p>
            <div style={st.modalFoot}>
              <button
                type="button"
                style={st.btnSecondary}
                disabled={!!togglingId}
                onClick={() => setConfirmToggle(null)}
              >
                Hủy
              </button>
              <button
                type="button"
                style={st.btnPrimary}
                disabled={!!togglingId}
                onClick={() => handleToggle(confirmToggle)}
              >
                {togglingId && <span style={st.spinner} aria-hidden="true" />}
                {confirmToggle.is_active ? "Ngừng hoạt động" : "Kích hoạt"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* [6] Dialog chặn xoá loại (đang được đơn vị dùng) */}
      {blockMsg && (
        <div style={st.overlay} onClick={() => setBlockMsg(null)}>
          <div
            style={st.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="blockDeleteTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={st.modalTitle} id="blockDeleteTitle">Không thể xoá loại đơn vị</h2>
            <p style={{ margin: "0 0 16px", fontSize: "14px", color: "var(--nqh-gray-700, #374151)" }}>
              {blockMsg}
            </p>
            <div style={st.modalFoot}>
              <button type="button" style={st.btnPrimary} onClick={() => setBlockMsg(null)}>
                Đã hiểu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast (pattern window.nqhToast) */}
      {toast && (
        <div style={st.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  );
};

export default UnitTypesTab;
